import { destroy } from '@alltrails/core/utils/destroy';
import { post } from '@alltrails/core/utils/post';
import { adjustFromSameNominalTimeForUser } from '@alltrails/data-formatting/dateTimeConverters';
import type { CustomRouteMap } from '@alltrails/types/CustomRoute';
import type { Obstacle } from '@alltrails/types/Obstacle';
import type { Track, TrackId } from '@alltrails/types/Track';
import type { TrailId } from '@alltrails/types/TrailId';
import type TrailInfo from '@alltrails/types/TrailInfo';
import type TrailRating from '@alltrails/types/TrailRating';

export function removeTrack(trackId: TrackId) {
  return post(`/api/alltrails/maps/${trackId}/remove`, undefined);
}

export function deleteTrack(trackId: TrackId) {
  return destroy(`/api/alltrails/maps/${trackId}`);
}

export function setTrackAsVerified(track: Track, trailId: TrailId) {
  const trackTrailId = track.trailId ?? trailId;

  return post(`/api/alltrails/maps/${track.id}/verify`, undefined, {
    params: { trail_id: trackTrailId, use_contrib: true, preserve_trail_waypoints: true, ignore_map_waypoints: true }
  });
}

type UpdateTrackData = {
  name?: string;
  description?: string;
  activity?: { value: string };
  date?: Date;
  rating?: number;
  contentPrivacy?: string;
  comment?: string;
  hidden?: boolean;
  obstacleUids?: Obstacle[];
  trailInfoUids?: TrailInfo[];
  trailRatingUids?: TrailRating[];
};

type ServerTrackParams = {
  name?: string;
  description?: string;
  activity_uid?: string | { value: string };
  contentPrivacy?: string;
  rating?: number;
  trail_id?: TrailId | null;
  detail?: string;
  hidden?: boolean;
  new_created_date?: string;
  comment?: string;
  obstacle_uids?: Obstacle[];
  trailInfoUids?: TrailInfo[];
  trailRatingUids?: TrailRating[];
};

export function updateTrack(trackId: TrackId, data: UpdateTrackData, trailId?: TrailId) {
  const serverData: Partial<ServerTrackParams> = {
    trail_id: trailId,
    name: data.name,
    description: data.description,
    activity_uid: data.activity?.value || data.activity,
    contentPrivacy: data.contentPrivacy,
    rating: data.rating,
    hidden: data.hidden,
    comment: data.comment,
    obstacle_uids: data.obstacleUids,
    trailInfoUids: data.trailInfoUids,
    trailRatingUids: data.trailRatingUids,
    detail: 'deep'
  };

  if (data.date) {
    serverData.new_created_date = adjustFromSameNominalTimeForUser(data.date).toISOString();
  }
  // The `detail: 'deep'` param above causes the server to return the full CustomRouteMap shape.
  return post<{ maps: CustomRouteMap[] }>(`/api/alltrails/maps/${trackId}/metadata`, serverData);
}
